import type {
  UserProfile, Organization, Member, Post, Meeting, PaymentPeriod, FinancialYearSummary,
  SupplierInvoice, OtherCost, Folder, Document, Proposal, Attachment,
} from '../types/models';

export const DEMO_ORG_ID = 'demo';
export const DEMO_USER_ID = 'demo-me';

export interface DemoDocument extends Document {
  folderId: string;
}

export interface DemoFinancialYear extends FinancialYearSummary {
  supplierInvoices: SupplierInvoice[];
  otherCosts: OtherCost[];
}

export interface DemoStore {
  profile: UserProfile;
  org: Organization;
  members: Member[];
  posts: Post[];
  meetings: Meeting[];
  paymentPeriods: PaymentPeriod[];
  financialYears: DemoFinancialYear[];
  folders: Folder[];
  documents: DemoDocument[];
  proposals: Proposal[];
}

const DAY = 24 * 60 * 60 * 1000;
const daysFromNow = (days: number, hour = 18) => {
  const d = new Date(Date.now() + days * DAY);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
};
const dateOnly = (iso: string) => iso.slice(0, 10);

export const demoFileUrl = (fileName: string) =>
  `data:text/plain;charset=utf-8,${encodeURIComponent(`${fileName}\n\nExempelfil från Test förening.`)}`;

const demoAttachment = (id: string, fileName: string, fileSize: number): Attachment =>
  ({ id, fileName, fileSize, downloadUrl: demoFileUrl(fileName) });

const seedMembers: Omit<Member, 'createdAt' | 'isPending' | 'userId'>[] = [
  { id: 'm-1', email: 'anna.lindqvist@example.se', fullName: 'Anna Lindqvist', phone: '070-123 45 01', propertyNumber: 'Björkvik 1:4', sharePercentage: 14, role: 'board_member' },
  { id: 'm-2', email: 'erik.johansson@example.se', fullName: 'Erik Johansson', phone: '070-123 45 02', propertyNumber: 'Björkvik 1:5', sharePercentage: 12, role: 'board_member' },
  { id: 'm-3', email: 'maria.berg@example.se', fullName: 'Maria Berg', phone: '070-123 45 03', propertyNumber: 'Björkvik 1:7', sharePercentage: 11, role: 'member' },
  { id: 'm-4', email: 'johan.nilsson@example.se', fullName: 'Johan Nilsson', phone: '070-123 45 04', propertyNumber: 'Björkvik 1:9', sharePercentage: 10, role: 'member' },
  { id: 'm-5', email: 'karin.ek@example.se', fullName: 'Karin Ek', phone: '070-123 45 05', propertyNumber: 'Björkvik 2:1', sharePercentage: 13, role: 'member' },
  { id: 'm-6', email: 'lars.holm@example.se', fullName: 'Lars Holm', phone: '070-123 45 06', propertyNumber: 'Björkvik 2:3', sharePercentage: 15, role: 'member' },
  { id: 'm-7', email: 'sofia.andersson@example.se', fullName: 'Sofia Andersson', phone: '070-123 45 07', propertyNumber: 'Björkvik 2:6', sharePercentage: 13, role: 'member' },
];

export function createDemoStore(viewer?: { fullName: string; email: string }): DemoStore {
  const profile: UserProfile = {
    id: DEMO_USER_ID,
    fullName: viewer?.fullName || 'Testanvändare',
    email: viewer?.email || 'test@testforening.se',
    phone: '',
  };
  const createdAt = daysFromNow(-120);
  const members: Member[] = [
    { id: 'm-me', userId: DEMO_USER_ID, email: profile.email, fullName: profile.fullName, phone: '', propertyNumber: 'Björkvik 1:2', sharePercentage: 12, role: 'board_member', createdAt, isPending: false },
    ...seedMembers.map(m => ({ ...m, userId: `u-${m.id}`, createdAt, isPending: false })),
  ];
  const author = (m: Member) => ({ id: m.userId, email: m.email, fullName: m.fullName });
  const year = new Date().getFullYear();

  const upcoming: Meeting = {
    id: 'mt-1', title: 'Årsstämma', description: 'Genomgång av verksamhetsåret, budget och val av styrelse.',
    location: 'Bygdegården, Björkvik', meetingDate: daysFromNow(14), createdAt: daysFromNow(-7),
    notificationChannel: 'email', attachments: [demoAttachment('a-1', 'Kallelse årsstämma.pdf', 84_000)],
    rsvps: [
      { id: 'r-1', userId: 'u-m-1', fullName: 'Anna Lindqvist', status: 'attending' },
      { id: 'r-2', userId: 'u-m-3', fullName: 'Maria Berg', status: 'maybe' },
    ],
    myRsvp: null, protocol: null,
  };
  const past: Meeting = {
    id: 'mt-2', title: 'Styrelsemöte – vårens vägunderhåll', description: 'Beslut om grusning och dikesrensning.',
    location: 'Hos Anna', meetingDate: daysFromNow(-30), createdAt: daysFromNow(-45),
    notificationChannel: 'sms', attachments: [], rsvps: [], myRsvp: 'attending',
    protocol: {
      id: 'pr-1', title: 'Protokoll styrelsemöte', status: 'draft', createdAt: daysFromNow(-29),
      content: '§1 Mötet öppnas.\n§2 Beslut: grusning av Björkviksvägen i maj.\n§3 Mötet avslutas.',
      approvals: [{ id: 'ap-1', userId: 'u-m-1', fullName: 'Anna Lindqvist', status: 'approved', comment: null, createdAt: daysFromNow(-28) }],
    },
  };
  const meetingPost = (m: Meeting): Post => ({
    id: `p-${m.id}`, title: m.title, content: m.description, postType: 'meeting', imageUrl: null,
    createdAt: m.createdAt, updatedAt: m.createdAt, author: author(members[1]), attachments: [], comments: [],
    meeting: { id: m.id, title: m.title, description: m.description, meetingDate: m.meetingDate, location: m.location },
  });

  const periodAmount = 4000;
  const paymentPeriods: PaymentPeriod[] = [{
    id: 'pp-1', title: `Medlemsavgift ${year}`, description: 'Årsavgift för vägunderhåll.', amount: periodAmount,
    dueDate: dateOnly(daysFromNow(-10)), createdAt: daysFromNow(-40), financialYearId: 'fy-1',
    memberPayments: members.map((m, i) => ({
      id: `mp-${m.id}`, memberId: m.id, memberName: m.fullName,
      amount: periodAmount * (m.sharePercentage / 100),
      isPaid: i % 3 !== 2, paidAt: i % 3 !== 2 ? daysFromNow(-15) : null,
      invoiceNumber: `${year}-${String(i + 1).padStart(3, '0')}`, invoiceDownloadUrl: null, reminderSentAt: null,
    })),
  }];

  return {
    profile,
    org: {
      id: DEMO_ORG_ID, name: 'Test förening', description: 'En påhittad vägförening för att prova tjänsten.',
      subscriptionTier: 'tier2', trialEndsAt: null, myRole: 'board_member', memberCount: members.length,
      subscriptionStatus: 'active', subscriptionPaidUntil: null,
      orgNumber: null, billingBank: null, billingAccount: null, billingPaymentDays: 30,
      paymentRemindersEnabled: true, paymentReminderDays: 7, isVerified: false,
    },
    members,
    posts: [
      meetingPost(upcoming),
      {
        id: 'p-1', title: 'Vägen grusas vecka 20', content: 'Entreprenören kommer måndag morgon. Undvik att parkera längs vägen.',
        postType: 'post', imageUrl: null, createdAt: daysFromNow(-3), updatedAt: daysFromNow(-3),
        author: author(members[1]), attachments: [], meeting: null,
        comments: [{ id: 'c-1', content: 'Tack för informationen!', createdAt: daysFromNow(-2), author: author(members[3]) }],
      },
    ],
    meetings: [upcoming, past],
    paymentPeriods,
    financialYears: [{
      id: 'fy-1', year, startDate: `${year}-01-01`, endDate: `${year}-12-31`, status: 'open', openingBalance: 25_000,
      totalIncome: 0, totalExpenses: 0, totalReceivables: 0, balance: 0, closedAt: null, reportDownloadUrl: null,
      createdAt: daysFromNow(-90),
      supplierInvoices: [{
        id: 'si-1', supplierName: 'Björkviks Grus AB', description: 'Grusning våren', amount: 12_500,
        invoiceDate: dateOnly(daysFromNow(-20)), dueDate: dateOnly(daysFromNow(10)), isPaid: false, paidAt: null,
        fileDownloadUrl: null, createdAt: daysFromNow(-20),
      }],
      otherCosts: [],
    }],
    folders: [
      { id: 'f-1', name: 'Stadgar', parentFolderId: null, documentCount: 1, createdAt },
      { id: 'f-2', name: 'Protokoll', parentFolderId: null, documentCount: 0, createdAt },
    ],
    documents: [{
      id: 'd-1', folderId: 'f-1', name: 'Stadgar Test förening.pdf', fileSize: 120_000, uploadedBy: 'Anna Lindqvist',
      downloadUrl: demoFileUrl('Stadgar Test förening.pdf'), createdAt,
    }],
    proposals: [],
  };
}
